// Polyfill: Promise.withResolvers
// Returns an object { promise, resolve, reject } where resolve/reject are the
// external resolvers for the promise. Define only if not already available.
type WithResolversSignature = <T = unknown>() => {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: unknown) => void;
};

const promiseTarget = Promise as unknown as { withResolvers?: WithResolversSignature };
if (typeof promiseTarget.withResolvers !== "function") {
  promiseTarget.withResolvers = function <T = unknown>() {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

// Polyfill: URL.parse
// Some libraries (e.g. older pdf.js bundles) call `URL.parse(str, base)` like
// Node's legacy url.parse. The browser's global `URL` constructor doesn't have
// `parse`, so we provide a lightweight shim that returns the properties the
// code expects (protocol, href, origin, hash, host, pathname, search, hostname, port).
type UrlParseResult = {
  href: string;
  protocol: string;
  origin: string;
  hash: string;
  host: string;
  hostname: string;
  port: string;
  pathname: string;
  search: string;
  auth: null | string;
  slashes: boolean;
};

const URLTarget = URL as unknown as { parse?: (input: string | URL, base?: string | URL) => UrlParseResult | null };
if (typeof URLTarget.parse !== "function") {
  URLTarget.parse = function (input: string | URL, base?: string | URL) {
    try {
      const u = input instanceof URL ? input : base ? new URL(String(input), String(base)) : new URL(String(input));
      return {
        href: u.href,
        protocol: u.protocol,
        origin: u.origin,
        hash: u.hash,
        host: u.host,
        hostname: u.hostname,
        port: u.port,
        pathname: u.pathname,
        search: u.search,
        auth: null,
        slashes: !!u.protocol,
      };
    } catch (_e) {
      return null;
    }
  };
}
